import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
    ShoppingCart, RefreshCw, Eye, RotateCcw, CheckCircle,
    XCircle, Clock, Euro, User, Mail, Phone, Calendar, Ticket, Truck,
    AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    paid: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    refunded: 'bg-purple-100 text-purple-800',
    payment_conflict: 'bg-orange-100 text-orange-800'
};

const statusLabels = {
    pending: 'Ausstehend',
    confirmed: 'Bestätigt',
    paid: 'Bezahlt',
    cancelled: 'Storniert',
    refunded: 'Erstattet',
    payment_conflict: '⚠ Zahlungs-Konflikt'
};

export const OrderManager = ({ eventId, eventTitle }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [refundLoading, setRefundLoading] = useState(false);
    const [markShippedLoading, setMarkShippedLoading] = useState(false);
    const [fulfillmentFilter, setFulfillmentFilter] = useState('all'); // 'all' | 'shipping' | 'pickup'
    // Laedt die Liste nicht, MUSS das sichtbar sein. Ein stiller Leerzustand
    // liest sich sonst als "keine Verkaeufe" — genau die Fehldiagnose, die
    // uns am 17.08.2026 einen halben Vormittag gekostet hat.
    const [loadError, setLoadError] = useState(null);

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchOrders();
    }, [eventId]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const url = eventId
                ? `${API_URL}/api/tickets/orders/event/${eventId}`
                : `${API_URL}/api/tickets/orders`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data);
            setLoadError(null);
        } catch (error) {
            console.error('Error fetching orders:', error);
            // 401 uebernimmt der globale Interceptor (Redirect zum Login).
            // Hier zaehlt alles andere: Backend down, 500, Netzfehler.
            setOrders([]);
            setLoadError(
                error.response?.status === 401
                    ? 'Sitzung abgelaufen — bitte neu anmelden.'
                    : `Bestellungen konnten nicht geladen werden (${
                          error.response?.status || 'keine Verbindung'
                      }). Die Zahlen unten sind daher NICHT aussagekraeftig.`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRefund = async (orderId) => {
        if (!window.confirm('Diese Bestellung wirklich erstatten? Der Betrag wird dem Kunden zurückgezahlt und die Tickets wieder freigegeben.')) {
            return;
        }

        setRefundLoading(true);
        try {
            await axios.post(
                `${API_URL}/api/tickets/orders/${orderId}/refund`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Erstattung erfolgreich');
            fetchOrders();
            setIsDetailsOpen(false);
        } catch (error) {
            console.error('Error refunding order:', error);
            alert('Fehler bei der Erstattung: ' + (error.response?.data?.detail || error.message));
        } finally {
            setRefundLoading(false);
        }
    };

    const handleMarkShipped = async (orderId) => {
        if (!window.confirm('Diese Bestellung als VERSANDT markieren? (rein informativ — keine Geld-/Ticketänderung)')) {
            return;
        }
        setMarkShippedLoading(true);
        try {
            await axios.post(
                `${API_URL}/api/tickets/orders/${orderId}/mark-shipped`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchOrders();
            setIsDetailsOpen(false);
        } catch (error) {
            console.error('Error marking shipped:', error);
            alert('Fehler beim Markieren: ' + (error.response?.data?.detail || error.message));
        } finally {
            setMarkShippedLoading(false);
        }
    };

    const openDetails = (order) => {
        setSelectedOrder(order);
        setIsDetailsOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calculate stats
    const stats = {
        total: orders.length,
        confirmed: orders.filter(o => o.order_status === 'confirmed').length,
        revenue: orders
            .filter(o => o.payment_status === 'paid' && o.order_status !== 'refunded')
            .reduce((sum, o) => sum + (o.total_amount || 0), 0),
        tickets: orders
            .filter(o => o.order_status === 'confirmed')
            .reduce((sum, o) => sum + o.items?.reduce((s, i) => s + (i.quantity || 0), 0), 0)
    };

    // Filter NUR für die Listen-Ansicht (Stats oben bleiben gesamt). Default = alle.
    const filteredOrders = fulfillmentFilter === 'all'
        ? orders
        : orders.filter(o => (o.fulfillment || 'online') === fulfillmentFilter);

    if (loading) {
        return <div className="text-center py-4">Laden...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Ladefehler — bewusst GANZ oben, vor den Zahlen */}
            {loadError && (
                <div className="border-2 border-red-300 bg-red-50 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="font-bold text-red-800">Daten nicht geladen</p>
                        <p className="text-sm text-red-700 mt-1">{loadError}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchOrders}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Erneut versuchen
                    </Button>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-sm text-gray-500">Bestellungen</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
                        <p className="text-sm text-gray-500">Bestätigt</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold">{stats.tickets}</p>
                        <p className="text-sm text-gray-500">Tickets verkauft</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">{stats.revenue.toFixed(2)} €</p>
                        <p className="text-sm text-gray-500">Umsatz</p>
                    </CardContent>
                </Card>
            </div>

            {/* Header */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    {eventTitle ? `Bestellungen: ${eventTitle}` : 'Alle Bestellungen'}
                </h3>
                <div className="flex items-center gap-2">
                    <select
                        value={fulfillmentFilter}
                        onChange={(e) => setFulfillmentFilter(e.target.value)}
                        className="p-2 border border-gray-200 rounded-lg text-sm"
                    >
                        <option value="all">Alle Bestellungen</option>
                        <option value="shipping">Nur Versand</option>
                        <option value="pickup">Nur Abholung</option>
                    </select>
                    <Button variant="outline" onClick={fetchOrders}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Aktualisieren
                    </Button>
                </div>
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-gray-500">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>
                            {loadError
                                ? 'Keine Daten — siehe Fehlermeldung oben.'
                                : fulfillmentFilter === 'all'
                                    ? 'Noch keine Bestellungen'
                                    : 'Keine Bestellungen in dieser Ansicht'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredOrders.map((order) => (
                        <Card key={order.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-mono text-sm font-bold">
                                                #{order.id?.slice(0, 8).toUpperCase()}
                                            </span>
                                            <Badge className={statusColors[order.order_status]}>
                                                {statusLabels[order.order_status] || order.order_status}
                                            </Badge>
                                            <Badge className={statusColors[order.payment_status]} variant="outline">
                                                {statusLabels[order.payment_status] || order.payment_status}
                                            </Badge>
                                            {order.fulfillment === 'pickup' && (
                                                <Badge className="bg-green-100 text-green-800">Abholung</Badge>
                                            )}
                                            {order.fulfillment === 'shipping' && !order.shipped_at && (
                                                <Badge className="bg-yellow-100 text-yellow-800">Versand</Badge>
                                            )}
                                            {order.fulfillment === 'shipping' && order.shipped_at && (
                                                <Badge className="bg-green-100 text-green-800">Versandt</Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 mb-1">
                                            {order.customer_first_name} {order.customer_last_name} • {order.customer_email}
                                        </p>
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="h-3 w-3" />
                                                {formatDate(order.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Ticket className="h-3 w-3" />
                                                {order.items?.reduce((s, i) => s + (i.quantity || 0), 0)} Tickets
                                            </span>
                                            <span className="flex items-center gap-1 font-medium text-black">
                                                <Euro className="h-3 w-3" />
                                                {order.total_amount?.toFixed(2)} €
                                            </span>
                                        </div>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => openDetails(order)}>
                                        <Eye className="h-4 w-4 mr-1" />
                                        Details
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Order Details Dialog */}
            <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Bestellung #{selectedOrder?.id?.slice(0, 8).toUpperCase()}
                            <Badge className={statusColors[selectedOrder?.order_status]}>
                                {statusLabels[selectedOrder?.order_status]}
                            </Badge>
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedOrder && (
                        <div className="space-y-6">
                            {/* Customer Info */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-bold mb-2 flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Kunde
                                </h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <p><strong>Name:</strong> {selectedOrder.customer_first_name} {selectedOrder.customer_last_name}</p>
                                    <p className="flex items-center gap-1">
                                        <Mail className="h-3 w-3" />
                                        {selectedOrder.customer_email}
                                    </p>
                                    {selectedOrder.customer_phone && (
                                        <p className="flex items-center gap-1">
                                            <Phone className="h-3 w-3" />
                                            {selectedOrder.customer_phone}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Versandadresse (nur bei Versand) */}
                            {selectedOrder.fulfillment === 'shipping' && (
                                <div className="bg-orange-50 p-4 rounded-lg">
                                    <h4 className="font-bold mb-2 flex items-center gap-2">
                                        <Truck className="h-4 w-4" />
                                        Versand
                                    </h4>
                                    {selectedOrder.address_purged ? (
                                        <p className="text-sm text-gray-500">Adresse nach DSGVO-Frist gelöscht.</p>
                                    ) : (
                                        <div className="text-sm space-y-0.5">
                                            <p>{selectedOrder.shipping_name}</p>
                                            <p>{selectedOrder.shipping_street}</p>
                                            {selectedOrder.shipping_addon && <p>{selectedOrder.shipping_addon}</p>}
                                            <p>{selectedOrder.shipping_zip} {selectedOrder.shipping_city}</p>
                                        </div>
                                    )}
                                    {selectedOrder.shipped_at && (
                                        <p className="text-xs text-green-700 mt-2">✓ Versandt: {formatDate(selectedOrder.shipped_at)}</p>
                                    )}
                                </div>
                            )}

                            {/* Event */}
                            <div>
                                <h4 className="font-bold mb-2">Event</h4>
                                <p>{selectedOrder.event_title}</p>
                            </div>

                            {/* Tickets */}
                            <div>
                                <h4 className="font-bold mb-2">Tickets</h4>
                                <div className="space-y-3">
                                    {selectedOrder.items?.map((item, idx) => (
                                        <div key={idx} className="border rounded-lg p-3">
                                            <div className="flex justify-between mb-2">
                                                <span className="font-medium">{item.ticket_type_name}</span>
                                                <span>{item.quantity}x {item.unit_price?.toFixed(2)} € = {item.total_price?.toFixed(2)} €</span>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                <p className="font-medium">Teilnehmer:</p>
                                                <ul className="list-disc list-inside">
                                                    {item.attendees?.map((att, i) => (
                                                        <li key={i}>
                                                            {att.first_name} {att.last_name}
                                                            {att.email && ` (${att.email})`}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total */}
                            <div className="border-t pt-4">
                                <div className="flex justify-between text-lg font-bold">
                                    <span>Gesamtbetrag:</span>
                                    <span>{selectedOrder.total_amount?.toFixed(2)} €</span>
                                </div>
                            </div>

                            {/* Timestamps */}
                            <div className="text-sm text-gray-500 space-y-1">
                                <p>Erstellt: {formatDate(selectedOrder.created_at)}</p>
                                {selectedOrder.paid_at && <p>Bezahlt: {formatDate(selectedOrder.paid_at)}</p>}
                                {selectedOrder.refunded_at && <p>Erstattet: {formatDate(selectedOrder.refunded_at)}</p>}
                            </div>

                            {/* Versand-Action: als versandt markieren (nur Versand, noch nicht versandt) */}
                            {selectedOrder.fulfillment === 'shipping' && !selectedOrder.shipped_at && (
                                <div className="border-t pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => handleMarkShipped(selectedOrder.id)}
                                        disabled={markShippedLoading}
                                        className="w-full"
                                    >
                                        {markShippedLoading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Truck className="h-4 w-4 mr-2" />
                                        )}
                                        Als versandt markieren
                                    </Button>
                                    <p className="text-xs text-gray-500 mt-2 text-center">
                                        Rein informativ (setzt nur „versandt am") — ändert weder Zahlung noch Tickets.
                                    </p>
                                </div>
                            )}

                            {/* Actions */}
                            {selectedOrder.payment_status === 'paid' && selectedOrder.order_status !== 'refunded' && (
                                <div className="border-t pt-4">
                                    <Button
                                        variant="destructive"
                                        onClick={() => handleRefund(selectedOrder.id)}
                                        disabled={refundLoading}
                                        className="w-full"
                                    >
                                        {refundLoading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <RotateCcw className="h-4 w-4 mr-2" />
                                        )}
                                        Bestellung erstatten
                                    </Button>
                                    <p className="text-xs text-gray-500 mt-2 text-center">
                                        Der Betrag wird über Stripe zurückerstattet und die Tickets werden wieder freigegeben.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrderManager;
